import * as logger from "@superbuilders/slog"
import type { AnyCondition, AssessmentItem, BaseRule, ResponseRule } from "./schema"

/**
 * Normalizes a string for comparison (trimming and optional case-insensitivity)
 */
function normalizeString(str: string, caseSensitive: boolean): string {
	const s = (str ?? "").trim()
	return caseSensitive ? s : s.toLowerCase()
}

/**
 * Builds a rounding function for qti-equal-rounded.
 * "decimalPlaces" rounds to N digits after the decimal point.
 * "significantFigures" rounds to N significant digits (the QTI default).
 */
function roundForCompare(rounding: {
	mode: "significantFigures" | "decimalPlaces"
	figures: number
}): (n: number) => number {
	if (rounding.mode === "decimalPlaces") {
		const factor = 10 ** rounding.figures
		return (n: number) => Math.round(n * factor) / factor
	}
	return (n: number) => {
		if (n === 0) return 0
		const exponent = Math.floor(Math.log10(Math.abs(n)))
		const factor = 10 ** (rounding.figures - 1 - exponent)
		return Math.round(n * factor) / factor
	}
}

/**
 * Evaluates a condition from the response processing rules against the user's responses.
 */
function checkCondition(
	condition: AnyCondition,
	item: AssessmentItem,
	responses: Record<string, string | string[]>,
	responseResults: Record<string, boolean>
): boolean {
	// 1. Recursive Logic (AND / OR / NOT)
	if (condition.type === "and") {
		const results = condition.conditions.map((c: AnyCondition) => checkCondition(c, item, responses, responseResults))
		const allTrue = results.every((r: boolean) => r === true)
		logger.debug("qti evaluator: checking AND condition", {
			numConditions: condition.conditions.length,
			results,
			allTrue
		})
		return allTrue
	}

	if (condition.type === "or") {
		const results = condition.conditions.map((c: AnyCondition) => checkCondition(c, item, responses, responseResults))
		const anyTrue = results.some((r: boolean) => r === true)
		logger.debug("qti evaluator: checking OR condition", {
			numConditions: condition.conditions.length,
			results,
			anyTrue
		})
		return anyTrue
	}

	if (condition.type === "not") {
		const result = checkCondition(condition.condition, item, responses, responseResults)
		logger.debug("qti evaluator: checking NOT condition", { result, negated: !result })
		return !result
	}

	// 2. Standard Match (correctness check)
	if (condition.type === "match") {
		const result = responseResults[condition.variable]
		logger.debug("qti evaluator: checking match condition", {
			variable: condition.variable,
			isCorrect: result,
			matches: result === true
		})
		return result === true
	}

	// 3. Match Specific Value (hardcoded value check)
	if (condition.type === "matchValue") {
		const userResponse = responses[condition.variable]
		let userValues: string[]
		if (Array.isArray(userResponse)) {
			userValues = userResponse
		} else if (userResponse) {
			userValues = [userResponse]
		} else {
			userValues = []
		}
		const matches = userValues.includes(condition.value)
		logger.debug("qti evaluator: checking matchValue condition", {
			variable: condition.variable,
			targetValue: condition.value,
			userValues,
			matches
		})
		return matches
	}

	// 4. String Match (text entry with case sensitivity)
	if (condition.type === "stringMatch") {
		const userResponse = responses[condition.variable]
		const val = Array.isArray(userResponse) ? userResponse[0] : userResponse
		if (!val) return false

		const u = normalizeString(String(val), condition.caseSensitive)
		const t = normalizeString(condition.value, condition.caseSensitive)
		const matches = u === t
		logger.debug("qti evaluator: checking stringMatch", {
			variable: condition.variable,
			caseSensitive: condition.caseSensitive,
			matches
		})
		return matches
	}

	// 5. Member (set logic for multi-select checkboxes)
	if (condition.type === "member") {
		const userResponse = responses[condition.variable]
		let userValues: string[]
		if (Array.isArray(userResponse)) {
			userValues = userResponse
		} else if (userResponse) {
			userValues = [userResponse]
		} else {
			userValues = []
		}
		const matches = userValues.includes(condition.value)
		logger.debug("qti evaluator: checking member", {
			variable: condition.variable,
			target: condition.value,
			userValues,
			matches
		})
		return matches
	}

	// 6. Numeric Equal (qti-equal / qti-equal-rounded against a base-value literal)
	if (condition.type === "numericEqual") {
		const userResponse = responses[condition.variable]
		const userVal = Array.isArray(userResponse) ? userResponse[0] : userResponse
		if (!userVal) return false

		const userNum = Number.parseFloat(userVal)
		const targetNum = Number.parseFloat(condition.value)
		if (!Number.isFinite(userNum) || !Number.isFinite(targetNum)) {
			logger.debug("qti evaluator: numericEqual non-numeric input", {
				variable: condition.variable,
				userVal,
				target: condition.value
			})
			return false
		}

		const apply = condition.rounding ? roundForCompare(condition.rounding) : (n: number) => n
		const u = apply(userNum)
		const t = apply(targetNum)
		// epsilon for floats; integer rounds collapse exactly so the same epsilon is harmless
		const matches = Math.abs(u - t) < 0.0001

		logger.debug("qti evaluator: checking numericEqual", {
			variable: condition.variable,
			baseType: condition.baseType,
			rounding: condition.rounding,
			userNum,
			targetNum,
			matches
		})
		return matches
	}

	// 7. Map Response (scoring logic with epsilon comparison)
	if (condition.type === "equalMapResponse") {
		const responseId = condition.variable
		const userResponse = responses[responseId]
		const responseDecl = item.responseDeclarations.find((rd) => rd.identifier === responseId)

		if (!userResponse || !responseDecl?.mapping) return false

		const userValues = Array.isArray(userResponse) ? userResponse : [userResponse]
		const valueMap = new Map(responseDecl.mapping.entries.map((e) => [e.key, e.value]))

		let sum = 0
		for (const val of userValues) {
			sum += valueMap.get(val) ?? responseDecl.mapping.defaultValue
		}

		// Float comparison safety (epsilon check)
		const target = Number(condition.value)
		const diff = Math.abs(sum - target)
		const matches = diff < 0.0001

		logger.debug("qti evaluator: checking equalMapResponse", {
			responseId,
			userValues,
			sum,
			target: condition.value,
			matches
		})
		return matches
	}

	return false
}

/**
 * Traverses a single response rule recursively to find matching feedback identifiers
 */
function evaluateRule(
	rule: ResponseRule,
	item: AssessmentItem,
	responses: Record<string, string | string[]>,
	responseResults: Record<string, boolean>
): string[] {
	const feedbackIds: string[] = []

	// Case 1: Direct Action (unconditional)
	if (rule.type === "action") {
		const action = rule.action
		if (
			action.type === "setOutcomeValue" &&
			(action.identifier === "FEEDBACK__OVERALL" ||
				action.identifier === "FEEDBACK__PEDAGOGY" ||
				action.identifier === "FEEDBACK")
		) {
			logger.debug("qti evaluator: executing action", { id: action.identifier, value: action.value })
			feedbackIds.push(action.value)
		}
		return feedbackIds
	}

	// Case 2: Condition Block (if/elseif/else)
	if (rule.type === "condition") {
		// Iterate through branches and stop at the first match
		for (const branch of rule.branches) {
			const isMatch = branch.condition ? checkCondition(branch.condition, item, responses, responseResults) : true // Else branch (undefined condition) always matches if reached

			logger.debug("qti evaluator: evaluating branch", {
				hasCondition: !!branch.condition,
				isMatch
			})

			if (isMatch) {
				// Execute actions in this branch
				const feedbackActions = branch.actions.filter(
					(r: BaseRule) =>
						r.type === "setOutcomeValue" &&
						(r.identifier === "FEEDBACK__OVERALL" ||
							r.identifier === "FEEDBACK__PEDAGOGY" ||
							r.identifier === "FEEDBACK")
				)
				for (const action of feedbackActions) {
					feedbackIds.push(action.value)
				}

				// Execute nested rules recursively
				if (branch.nestedRules) {
					for (const nested of branch.nestedRules) {
						const nestedIds = evaluateRule(nested, item, responses, responseResults)
						feedbackIds.push(...nestedIds)
					}
				}

				// CRITICAL: Stop after the first matching branch!
				// This prevents 'else' from executing if 'if' matched.
				break
			}
		}
	}

	return feedbackIds
}

/**
 * Traverses the response processing rules to find all matching feedback identifiers.
 */
export function evaluateFeedbackIdentifiers(
	item: AssessmentItem,
	responses: Record<string, string | string[]>,
	responseResults: Record<string, boolean>
): string[] {
	const processing = item.responseProcessing
	if (!processing) {
		logger.debug("qti evaluator: no response processing found")
		return []
	}

	logger.debug("qti evaluator: starting evaluation", {
		numRules: processing.rules.length,
		responseResults
	})

	const allFeedbackIds: string[] = []

	for (const rule of processing.rules) {
		const ids = evaluateRule(rule, item, responses, responseResults)
		allFeedbackIds.push(...ids)
	}

	logger.debug("qti evaluator: selected feedback identifiers", { feedbackIds: allFeedbackIds })
	return allFeedbackIds
}

/**
 * Legacy function for backwards compatibility - returns the first matching feedback identifier.
 * @deprecated Use evaluateFeedbackIdentifiers for full feedback support
 */
export function evaluateFeedbackIdentifier(
	item: AssessmentItem,
	responses: Record<string, string | string[]>,
	responseResults: Record<string, boolean>
): string | undefined {
	const ids = evaluateFeedbackIdentifiers(item, responses, responseResults)
	return ids[0]
}
